import { lazy } from 'react';
import { DETAIL_VIEW } from './detailViews.constants';

/**
 * Central registry of "detail views" — the command (create/edit) and query
 * (read) panels that pages open inside modals.
 *
 * Each key is `<entity>.<mode>`; the value is the lazily-imported widget. Every
 * registered view already owns its own modal chrome (SinglePager / MultiPager
 * layout) and accepts an `isSecondary` flag for stacking, so the host only has
 * to mount it.
 *
 * A view is added here exactly once; any page then opens it with
 * `useDetailViews().open('<entity>.<mode>', intent)` instead of importing the
 * widget and wiring its own open/close state. This is what lets a single query
 * panel (e.g. `client.query`) be reused across Clients, Inquiries, Framework
 * Agreements and Jobs without re-declaring the modal in each page.
 */
export const DETAIL_VIEWS = {
  // Client read panel — opened from Clients (with edit) and, going forward,
  // from Inquiries / Framework Agreements / Job flows (read-only).
  [DETAIL_VIEW.CLIENT_QUERY]: lazy(
    () => import('../../widgets/ClientDetails/query/ClientDetails')
  ),
  // Client create/edit wizard — opened only from the Clients listing.
  [DETAIL_VIEW.CLIENT_COMMAND]: lazy(
    () => import('../../widgets/ClientDetails/command/ClientAddOrUpdate')
  ),
  // Framework Agreement read panel (single-page details) — opened from the
  // Framework Agreements listing.
  [DETAIL_VIEW.FRAMEWORK_AGREEMENT_QUERY]: lazy(
    () =>
      import(
        '../../widgets/FrameworkAgreements/query/FrameworkAgreementDetails'
      )
  ),
  // Framework Agreement wizard — serves create (from a client) and edit; pass
  // `mode` defaults to command, `frameworkAgreementId` present when editing.
  [DETAIL_VIEW.FRAMEWORK_AGREEMENT_COMMAND]: lazy(
    () => import('../../widgets/FrameworkAgreements/FrameworkAgreementWizard')
  ),
  // Inquiry read panel. It owns its own secondary modals (quotation
  // add/edit/view, change-client, archive/reopen) internally — the host only
  // mounts the panel; pass `openQuotationRevisionId` to deep-link a quotation.
  [DETAIL_VIEW.INQUIRY_QUERY]: lazy(
    () => import('../../widgets/InquiryDetails/query/InquiryDetails')
  ),
  // Inquiry add/edit wizard — pass `isOpen: true`, explicit `isEditMode`, and
  // `clientId` (required for both add and edit).
  [DETAIL_VIEW.INQUIRY_COMMAND]: lazy(
    () => import('../../widgets/InquiryDetails/command/AddEditInquiry')
  ),
};

export default DETAIL_VIEWS;
